import {
  AnalyticsEventNameType,
  CreateOauthAttempt200Response,
  DataImport,
  DataSource,
  DataSourceType,
  OauthAttemptStatus,
} from "@coeff/api";
import { COLORS, WARNING_MESSAGES, WARNING_TITLES } from "@coeff/utils";
import { useQuery } from "@tanstack/react-query";
import { Layout, Upload } from "antd";
import { RcFile } from "antd/es/upload";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  Button,
  ConfirmBox,
  DotsLoader,
  ErrorMessage,
  Graphics,
  Input,
  SecurityInfo,
  WarningMessage,
  Icons,
  Logos,
  FlexRow,
} from "../../../common";
import {
  useApiClient,
  useEventManagerContext,
  useGasServerContext,
  useMessage,
} from "../../../globals/contexts";
import {
  BaseAuthorizeLocationState,
  QueryCacheKey,
  useAuthContext,
  WithPostDataSourceCreationProps,
} from "../../context/AuthContext";

import { LimitSchemasInput } from "./LimitSchemasInput";

const { BigQuery } = Logos;
const { ChevronDown, ConnectPlugIcon } = Icons;

const { Content, Footer } = Layout;

const PROJECT_ID_DOC_URL = "https://support.google.com/googleapi/answer/7014113?hl=en";

// How often to poll the OAuth attempt status while authorization is in progress.
const OAUTH_ATTEMPT_POLL_INTERVAL_MS = 2_000;

type AuthMode = "oauth" | "service_account";

const StyledContent = styled(Content)`
  && {
    height: 100%;
    min-height: calc(100vh - 128px);
    margin-top: 64px;
    background-color: ${COLORS.white};
    padding: 14px 0px;

    p {
      margin-top: 0px;
    }

    .datasourceLogo {
      margin: auto;
      display: block;
    }

    .graphicsLogo {
      margin-top: 1rem;

      .datasourceLogo {
        width: 60px;
        height: 60px;
      }
    }

    .contactLink {
      color: inherit;
      text-decoration: underline;
      text-decoration-style: dashed;
      text-underline-offset: 3px;
    }
  }
`;

const StyledFooter = styled(Footer)`
  position: sticky;
  bottom: 0px;
  padding: 0px;
  background: ${COLORS.white};
  z-index: 50;
`;

const StyledLabel = styled.div`
  display: block;
  width: auto;
  margin: 15px;
  color: ${COLORS.black85};

  .coeff__input {
    height: 32px;
    margin: 6px auto;
    border-color: ${COLORS.black15};
  }
`;

const UploadButton = styled.button<{ error?: boolean }>`
  background: ${COLORS.coeblue};
  border-radius: 4px;
  border: ${({ error }) => (error ? "2px solid " + COLORS.red : "none")};
  margin-left: 14px;
  padding: 10px;
  min-width: 268px;
  font-weight: bold;
  color: ${COLORS.coeblue4};
`;

const InnerPanel = styled.div`
  background: ${COLORS.coeblue};
  border-radius: 4px;
  padding: 10px;
  margin: 14px;

  .title {
    text-align: center;
    color: ${COLORS.black85};
    font-weight: bold;
    padding-bottom: 10px;
  }

  .description {
    text-align: center;
    color: ${COLORS.black45};
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
  }

  label {
    display: flex;
    width: auto;
    color: ${COLORS.black85};
    font-weight: 500;
    align-items: center;
    margin: 10px 0px;

    .labelText {
      width: 80px;
    }

    .coeff__input {
      height: 32px;
      margin: auto;
      border-color: ${COLORS.black15};
    }
  }
`;

const ButtonSection = styled.section<{ noBoxShadow?: boolean }>`
  width: 100%;
  display: flex;
  position: sticky;
  bottom: 0px;
  padding: 20px 4px 10px;
  background: white;
  justify-content: center;
  z-index: 25;
  ${({ noBoxShadow }) => {
    if (noBoxShadow) {
      return "";
    }

    return "box-shadow: 0px -10px 10px -10px #dddddd;";
  }}

  .ant-btn {
    flex: 1;
    margin: 0px 10px;
    max-width: 200px;

    svg {
      width: 1rem;
      margin-right: 4px;
      position: relative;
      top: 2px;
    }
  }
`;

const StyledUpload = styled(Upload)`
  .anticon-paper-clip {
    display: none;
  }

  .ant-upload button {
    cursor: pointer;
  }

  .ant-upload-list-item-info,
  .ant-upload-list-item-info:hover {
    background-color: ${COLORS.white};
    color: ${COLORS.coeblue4};

    .ant-upload-list-item-card-actions {
      button,
      button:hover {
        background-color: ${COLORS.white};
        opacity: 1;
      }
    }
  }
`;

const StyledLink = styled.span`
  color: ${COLORS.coeblue4};
  font-weight: bold;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const OAuthCard = styled.div`
  background: ${COLORS.white};
  border-radius: 4px;
  padding: 16px;
  margin: 14px;

  .help-text {
    color: ${COLORS.black45};
    font-size: 12px;
    margin-top: 4px;

    a {
      color: ${COLORS.coeblue4};
    }
  }
`;

const OAuthButtonWrapper = styled.div`
  margin-top: 16px;

  .ant-btn {
    width: 100%;
  }

  .cancel-link {
    display: block;
    text-align: center;
    margin-top: 8px;
    color: ${COLORS.coeblue4};
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

type UiErrors = {
  url?: boolean;
  username?: boolean;
  clientPrivateKey?: boolean;
  gcpProjectId?: boolean;
  serviceAccountEmail?: boolean;
};

export interface AuthorizeBigQueryLocationState extends BaseAuthorizeLocationState {
  dataImport?: DataImport;
  dataSource?: DataSource;
  fromConnectedSource?: boolean;
  fromOnboarding?: boolean;
  redirect?: string;
  redirectOnBack?: string;
  fromServiceAccountSwitch?: boolean; // True when user clicked "Service Account" link
  dataImportRecipeNavigationProps?: any;
}

export interface AuthorizeBigQueryContentProps {
  dataSource?: DataSource;
  isEditConnectionId?: string;
  fromConnectedSource?: boolean;
  fromOnboarding?: boolean;
  redirect?: string;
  redirectOnBack?: string;
  fromServiceAccountSwitch?: boolean;
  onComplete?: (dataSource: DataSource) => void;
  onCancel?: () => void;
  // Host-supplied UI slot rendered above the form. The content owns the schema
  // state and hands `setAvailableSchemas` + `isEditConnectionId` to the consumer
  // so the host app decides what to render (e.g. the addon's DataSourceItem in
  // edit mode, or a Graphics header in the webapp) without prop-drilling a typed
  // component.
  renderDataSourceItem?: (args: {
    dataSource?: DataSource;
    setAvailableSchemas: (schemas: string[]) => void;
    isEditConnectionId: boolean;
  }) => React.ReactNode;
  // Other props
  event_props?: { email?: string; domain?: string };
  dataSources?: DataSource[];
  initPostDataSourceCreation?: (args: {
    dataSource: DataSource;
    onComplete: (dataSource: DataSource) => void;
  }) => void;
  navigateOnDataSourceUpsert?: (dataSource: DataSource) => void;
}

/**
 * AuthorizeBigQueryContent - Core BigQuery authorization component.
 *
 * This component handles BigQuery OAuth and Service Account authentication.
 * It's designed to work with AuthContext for API calls and tracking.
 */
export const AuthorizeBigQueryContent: React.FC<AuthorizeBigQueryContentProps> = ({
  dataSource: initialDataSource,
  isEditConnectionId = "",
  fromConnectedSource = false,
  fromOnboarding = false,
  redirect,
  redirectOnBack,
  fromServiceAccountSwitch = false,
  onComplete,
  onCancel,
  renderDataSourceItem,
  event_props = {},
  dataSources = [],
  initPostDataSourceCreation,
  navigateOnDataSourceUpsert: navigateOnDataSourceUpsertProp,
}) => {
  const apiClient = useApiClient();
  const { track } = useEventManagerContext();
  const message = useMessage();
  const gasServerCtx = useGasServerContext();
  const {
    router,
    sentryCapture,
    getOAuthAuthorizationUrl,
    buildPPressMetadata,
    getDataSourceApiErrorMessage,
  } = useAuthContext();

  // For data sources query - use passed dataSources if available, otherwise empty array
  const dataSourceList = dataSources;

  // Fetch data source details if editing
  const { data: dataSource } = useQuery(
    [QueryCacheKey.DATA_SOURCE, initialDataSource?.data_source_id],
    () =>
      apiClient
        .getDataSource({
          data_source_id: initialDataSource?.data_source_id!,
        })
        .then(response => response.data.data_source),
    {
      enabled: Boolean(initialDataSource?.data_source_id && apiClient),
      initialData: initialDataSource,
      onSuccess: (dataSource: DataSource) => {
        setIsShared(dataSource?.is_shared_with_domain ?? false);
        // Set auth mode based on existing credentials
        if (dataSource?.credentials?.client_key_json) {
          setAuthMode("service_account");
        } else if (dataSource?.credentials?.oauth2_credentials_json) {
          setAuthMode("oauth");
        }
      },
      onError: (error: Error) => {
        console.error("Error fetching data source", error);
        sentryCapture?.({ error });
      },
    }
  );

  // Determine initial auth mode
  const getInitialAuthMode = (): AuthMode => {
    // If coming from service account switch, use service account mode
    if (fromServiceAccountSwitch) {
      return "service_account";
    }
    // If editing an existing connection
    if (initialDataSource) {
      if (initialDataSource.credentials?.client_key_json) {
        return "service_account";
      }
      // OAuth connections default to oauth mode
      return "oauth";
    }
    // New connections default to OAuth mode
    return "oauth";
  };

  const [authMode, setAuthMode] = useState<AuthMode>(getInitialAuthMode);

  // Service Account form state
  const [gcpProjectId, setGcpProjectId] = useState<string>(
    dataSource?.credentials?.client_key_json
      ? dataSource.credentials.client_key_json["project_id"]
      : ""
  );

  const [serviceAccountEmail, setServiceAccountEmail] = useState<string>(
    dataSource?.credentials?.client_key_json
      ? dataSource.credentials.client_key_json["client_email"]
      : ""
  );

  const [clientKeyJson, setClientKeyJson] = useState<Record<string, string>>();

  const [showUploadJsonButton, setShowUploadJsonButton] = useState(true);

  const [isShared, setIsShared] = useState(dataSource?.is_shared_with_domain || false);

  const [loading, setLoading] = useState(false);

  const [connectionError, setConnectionError] = useState<string | null>(null);

  const [connectionErrorTitle, setConnectionErrorTitle] = useState<string>("");

  const [uiErrors, setUiErrors] = useState<UiErrors>({});

  const [showServiceAccountCredentialsHelp, setShowServiceAccountCredentialsHelp] = useState(false);

  const [showHelpPanel, setShowHelpPanel] = useState(false);

  const readOnly = dataSource?.user ? dataSource.user.email !== event_props.email : false;

  const [showAdvancedSettings, setShowAdvancedSettings] = useState(
    Boolean(dataSource?.credentials?.selected_schemas?.length)
  );

  const [schemaLoading, setSchemaLoading] = useState(false);

  const [schemaError, setSchemaError] = useState<null | string>(null);

  const [availableSchemas, setAvailableSchemas] = useState<string[]>([]);

  const [selectedSchemas, setSelectedSchemas] = useState<string[]>(
    dataSource?.credentials?.selected_schemas || []
  );

  // OAuth-specific state
  const [oauthProjectId, setOauthProjectId] = useState<string>(
    dataSource?.ppress_metadata?.gcloud_project_id || ""
  );
  const [isOAuthInProgress, setIsOAuthInProgress] = useState(false);
  const [oauthAttemptId, setOauthAttemptId] = useState<string>();
  const [authTab, setAuthTab] = useState<Window | null>();
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [isOAuthCompleted, setIsOAuthCompleted] = useState(false);
  const [authorizedDataSourceId, setAuthorizedDataSourceId] = useState<string>();

  const navigateOnDataSourceUpsert = (ds: DataSource) => {
    if (navigateOnDataSourceUpsertProp) {
      navigateOnDataSourceUpsertProp(ds);
    } else if (onComplete) {
      onComplete(ds);
    }
  };

  // Poll OAuth attempt status
  const { data: oauthAttemptResponse } = useQuery(
    [QueryCacheKey.OAUTH_ATTEMPT, oauthAttemptId],
    () => apiClient.getOauthAttempt({ oauth_attempt_id: oauthAttemptId! }),
    {
      enabled: Boolean(isOAuthInProgress && oauthAttemptId && apiClient),
      refetchInterval: (data: any) =>
        data?.data?.oauth_attempt?.status !== OauthAttemptStatus.InProgress
          ? false
          : OAUTH_ATTEMPT_POLL_INTERVAL_MS,
    }
  );

  // Handle OAuth attempt status changes
  useEffect(() => {
    if (oauthAttemptResponse?.data?.oauth_attempt.status === OauthAttemptStatus.Success) {
      setOauthAttemptId(undefined);
      setAuthorizedDataSourceId(oauthAttemptResponse?.data.oauth_attempt.data_source_id);
      setIsOAuthCompleted(true);
      setIsOAuthInProgress(false);
      setConnectionError(null);
    } else if (oauthAttemptResponse?.data?.oauth_attempt.status === OauthAttemptStatus.Failure) {
      setOauthAttemptId(undefined);
      const error_message = oauthAttemptResponse?.data.oauth_attempt.error_message ?? "";
      setConnectionError(error_message);
      setConnectionErrorTitle("Unable to connect to BigQuery");
      setIsOAuthInProgress(false);

      track?.("import_oauth_failed", {
        import_data_source: "bigquery",
        data_source_id: isEditConnectionId,
        reason: error_message,
      });

      if (!isEditConnectionId) {
        track?.("conn_create_failed", {
          import_data_source: "bigquery",
          data_source_id: isEditConnectionId,
          reason: error_message,
        });
      }
    }
  }, [oauthAttemptResponse]);

  // Handle OAuth completion - find and process the new data source
  useEffect(() => {
    if (!isOAuthCompleted) {
      return;
    }

    authTab?.close();

    // Try to find the new dataSource from the list
    let newDataSource = authorizedDataSourceId
      ? dataSourceList.find(ds => ds.data_source_id === authorizedDataSourceId)
      : dataSourceList.find(ds => ds.data_source_type === DataSourceType.Bigquery);

    // If dataSource not found in list but we have authorizedDataSourceId, create a minimal object
    if (!newDataSource && authorizedDataSourceId) {
      newDataSource = {
        data_source_id: authorizedDataSourceId,
        data_source_type: DataSourceType.Bigquery,
        data_source_name: "BigQuery",
      } as DataSource;
    }

    if (!newDataSource) {
      return;
    }

    track?.("import_oauth_done", {
      import_data_source: "bigquery",
      data_source_id: isEditConnectionId,
    });

    if (!isEditConnectionId) {
      track?.("conn_create_done", {
        import_data_source: "bigquery",
        data_source_id: newDataSource.data_source_id,
      });
    }

    if (isEditConnectionId) {
      message?.success("Connection saved");
      navigateOnDataSourceUpsert(newDataSource);
    } else if (initPostDataSourceCreation) {
      initPostDataSourceCreation({
        dataSource: newDataSource,
        onComplete: ds => navigateOnDataSourceUpsert(ds),
      });
    } else {
      // Hosts that don't supply initPostDataSourceCreation (e.g. webapp) navigate directly.
      navigateOnDataSourceUpsert(newDataSource);
    }
  }, [dataSourceList, isOAuthCompleted]);

  // Close auth tab on completion or error
  useEffect(() => {
    if (authTab && (isOAuthCompleted || connectionError)) {
      authTab.close();
    }
  }, [isOAuthCompleted, connectionError]);

  // Track page view
  useEffect(() => {
    if (!isEditConnectionId) {
      track?.("conn_create_viewed", {
        import_data_source: "bigquery",
        event_from: fromConnectedSource
          ? "add_connection"
          : fromOnboarding
          ? "app_onboarding"
          : "add_import",
        data_source_id: isEditConnectionId,
      });
    }
  }, []);

  useEffect(() => {
    if (isEditConnectionId && apiClient) {
      reloadSchema();
    }
  }, [isEditConnectionId]);

  // OAuth click handler
  const onOAuthClick = async () => {
    if (!oauthProjectId) {
      setConnectionError("Please enter a GCP Project ID");
      setConnectionErrorTitle("GCP Project ID required");
      return;
    }

    setIsOAuthInProgress(true);
    setPopupBlocked(false);
    setConnectionError(null);

    if (!isEditConnectionId) {
      track?.("conn_create_started", {
        import_data_source: "bigquery",
        conn_data_source: "bigquery",
        data_source_id: isEditConnectionId,
      });
    }

    track?.("import_oauth_started", {
      import_data_source: "bigquery",
      conn_data_source: "bigquery",
      data_source_id: isEditConnectionId,
    });

    try {
      if (!apiClient || !getOAuthAuthorizationUrl || !buildPPressMetadata) {
        throw new Error("Missing required dependencies");
      }

      // Create OAuth attempt
      const {
        data: { oauth_attempt },
      } = await apiClient.createOauthAttempt({
        create_oauth_attempt_request: {
          data_source_type: DataSourceType.Bigquery,
          data_source_id: isEditConnectionId || undefined,
        },
      });

      // Get authorization URL
      const ppress_params = { gcloud_project_id: oauthProjectId };
      const ppress_metadata = buildPPressMetadata({
        dataSourceType: DataSourceType.Bigquery,
        ppress_params,
      });

      const authorizationUrl = await getOAuthAuthorizationUrl(apiClient, {
        email: event_props.email,
        oauthAttemptId: oauth_attempt.oauth_attempt_id,
        dataSourceId: isEditConnectionId || undefined,
        dataSourceType: DataSourceType.Bigquery,
        ppress_params,
        ppress_metadata,
      });

      // Open auth popup
      const platformAttributes = gasServerCtx?.gasServer?.getPlatformAttributes?.();
      const isDesktopExcel = Boolean(platformAttributes?.excel_desktop_version);

      const newTab = window.open(authorizationUrl, "_blank");

      if (!newTab || newTab.closed || typeof newTab.closed === "undefined") {
        track?.(AnalyticsEventNameType.AuthorizationWindowPopupBlocked, {
          data_source: DataSourceType.Bigquery,
          data_source_id: isEditConnectionId,
        });
        if (!isDesktopExcel) {
          setPopupBlocked(true);
          setIsOAuthInProgress(false);
          return;
        }
      }

      newTab?.focus();
      setAuthTab(newTab);
      setOauthAttemptId(oauth_attempt.oauth_attempt_id);
    } catch (error: unknown) {
      message?.error("Error initializing connection. Try again.");

      track?.("import_oauth_failed", {
        import_data_source: "bigquery",
        data_source_id: isEditConnectionId,
        reason: error instanceof Error ? error.message : String(error),
      });

      if (!isEditConnectionId) {
        track?.("conn_create_failed", {
          import_data_source: "bigquery",
          data_source_id: isEditConnectionId,
          reason: error instanceof Error ? error.message : String(error),
        });
      }

      setIsOAuthInProgress(false);
    }
  };

  const cancelOAuth = () => {
    setIsOAuthInProgress(false);
    setOauthAttemptId(undefined);
    authTab?.close();
  };

  const handleSwitchToServiceAccount = () => {
    setAuthMode("service_account");
    setConnectionError(null);
  };

  const handleSwitchToOAuth = () => {
    setAuthMode("oauth");
    setConnectionError(null);
  };

  const onFileDelete = () => {
    setShowUploadJsonButton(true);
    setClientKeyJson(undefined);
  };

  const beforeUpload = (file: RcFile): boolean => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        if (e.target && e.target.result) {
          const jsonObj = JSON.parse(e.target.result.toString());
          setClientKeyJson(jsonObj);
          setShowUploadJsonButton(false);
        }
      } catch (error) {
        setUiErrors({ ...uiErrors, clientPrivateKey: true });
      }
    };
    reader.readAsText(file);

    // Prevent upload
    return false;
  };

  const validateConnectionDetails = () => {
    setUiErrors({});
    const newUiErrors: UiErrors = {};

    if (!gcpProjectId) {
      newUiErrors.gcpProjectId = true;
      setConnectionErrorTitle("GCP Project ID invalid");
      setConnectionError(
        "The uploaded JSON key is for a different GCP Project ID. Update the Project ID or ensure that you are uploading the right key."
      );
    }

    if (!serviceAccountEmail) {
      newUiErrors.serviceAccountEmail = true;
      setConnectionErrorTitle("Service Account Email invalid");
      setConnectionError(
        "The uploaded JSON key provided by you is for a different Service Account email. Update the Email or ensure that you are uploading the right key."
      );
    }

    if (!serviceAccountEmail && !gcpProjectId) {
      setConnectionErrorTitle("Invalid GCP Project ID and Email");
      setConnectionError(
        "The uploaded JSON key provided by you is for a different GCP Project ID and Service Account email. Update the Project ID and Email or ensure that you are uploading the right key."
      );
    }

    if (!clientKeyJson) {
      newUiErrors.clientPrivateKey = true;
      setConnectionErrorTitle("JSON key not uploaded");
      setConnectionError("Please upload the JSON key for your Service Account.");
    }

    if (gcpProjectId && serviceAccountEmail && clientKeyJson) {
      const jsonGcpProjectId = clientKeyJson["project_id"];
      const jsonServiceAccountEmail = clientKeyJson["client_email"];
      if (jsonGcpProjectId !== gcpProjectId) {
        newUiErrors.gcpProjectId = true;
        setConnectionErrorTitle("GCP Project ID invalid");
        setConnectionError(
          "The uploaded JSON key is for a different GCP Project ID. Update the Project ID or ensure that you are uploading the right key."
        );
      }
      if (jsonServiceAccountEmail !== serviceAccountEmail) {
        newUiErrors.serviceAccountEmail = true;
        setConnectionErrorTitle("Service Account Email invalid");
        setConnectionError(
          "The uploaded JSON key provided by you is for a different Service Account email. Update the Email or ensure that you are uploading the right key."
        );
      }
      if (jsonServiceAccountEmail !== serviceAccountEmail && jsonGcpProjectId !== gcpProjectId) {
        setConnectionErrorTitle("Invalid GCP Project ID and Email");
        setConnectionError(
          "The uploaded JSON key provided by you is for a different GCP Project ID and Service Account email. Update the Project ID and Email or ensure that you are uploading the right key."
        );
      }
    }

    const isValid = Object.keys(newUiErrors).length <= 0;
    setUiErrors(newUiErrors);

    return isValid;
  };

  const reloadSchema = async () => {
    if (!apiClient || !getDataSourceApiErrorMessage) {
      return;
    }

    setSchemaLoading(true);
    try {
      if (isEditConnectionId) {
        const response = await apiClient.sqlGetSchema({
          data_source_type: DataSourceType.Bigquery,
          sql_get_schema_request: {
            data_source_id: dataSource?.data_source_id!,
            force_update_cache: true,
            show_all_available_schemas: true,
          },
        });
        if (response?.data?.available_schemas) {
          const schemas = response?.data?.available_schemas ?? [];
          setAvailableSchemas(schemas);
        }
      }
      setSchemaLoading(false);
    } catch (error) {
      sentryCapture?.({
        error,
        name: `AuthorizeBigQuery: POST /api/data_source_apis/bigquery/get_schema`,
      });

      setSchemaError(
        getDataSourceApiErrorMessage(error) ||
          "Oops, error occurred while reloading datasets. Please try again."
      );

      setSchemaLoading(false);
    }
  };

  const connectBigQuery = async () => {
    if (!apiClient || !getDataSourceApiErrorMessage) {
      return;
    }

    setConnectionError(null);

    if (!validateConnectionDetails()) {
      return;
    }

    track?.(isEditConnectionId ? "conn_edit_started" : "conn_create_started", {
      import_data_source: "bigquery",
      conn_data_source: "bigquery",
      edited_sharing: isEditConnectionId
        ? isShared !== dataSource?.is_shared_with_domain
        : undefined,
      data_source_id: isEditConnectionId,
    });

    setLoading(true);

    try {
      const credentials: Record<string, unknown> = {};

      credentials["client_key_json"] = clientKeyJson;
      credentials["selected_schemas"] = selectedSchemas;

      const { data: checkResp } = await apiClient.checkConnection({
        data_source_type: DataSourceType.Bigquery,
        check_connection_request: { credentials },
      });

      if (checkResp.valid) {
        const dataSourceBody: DataSource = {
          ...dataSource,
          data_source_type: "bigquery",
          data_source_name: dataSource?.data_source_name || "BigQuery",
          credentials,
          is_shared_with_domain: isShared,
        };

        const { data: dataSourceResponse } = await (isEditConnectionId
          ? apiClient.updateDataSource({
              data_source_id: isEditConnectionId,
              update_data_source_request: {
                data_source: dataSourceBody,
              },
            })
          : apiClient.createDataSource({
              create_data_source_request: { data_source: dataSourceBody },
            }));

        const data_source = dataSourceResponse.data_source;

        track?.(isEditConnectionId ? "conn_edit_done" : "conn_create_done", {
          import_data_source: "bigquery",
          edited_sharing: isEditConnectionId
            ? isShared !== dataSource?.is_shared_with_domain
            : undefined,
          data_source_id: isEditConnectionId,
        });

        if (isEditConnectionId) {
          message?.success("Connection saved");
          navigateOnDataSourceUpsert(data_source);
        } else if (initPostDataSourceCreation) {
          initPostDataSourceCreation({
            dataSource: data_source,
            onComplete: dataSource => navigateOnDataSourceUpsert(dataSource),
          });
        } else {
          // Hosts that don't supply initPostDataSourceCreation (e.g. webapp) navigate directly.
          navigateOnDataSourceUpsert(data_source);
        }
      } else if (checkResp.error) {
        track?.(isEditConnectionId ? "conn_edit_failed" : "conn_create_failed", {
          import_data_source: "bigquery",
          data_source_id: isEditConnectionId,
          reason: checkResp.error,
        });
        setConnectionError(checkResp.error);
        setConnectionErrorTitle("Couldn't connect to BigQuery");
      } else {
        setConnectionError("Please check the connection information below and try again.");
        setConnectionErrorTitle("Couldn't connect to BigQuery");
      }
    } catch (error) {
      sentryCapture?.({
        error,
        name: `AuthorizeBigQuery: POST /api/data_source_apis/bigquery/check_connection`,
      });

      setConnectionError(
        getDataSourceApiErrorMessage(error) ||
          "Please check the connection information below and try again."
      );

      setConnectionErrorTitle("Couldn't connect to BigQuery");
    }
    setLoading(false);
  };

  const handleBack = () => {
    if (redirectOnBack && onCancel) {
      onCancel();
    } else if (router?.back) {
      router.back();
    }
  };

  const renderOAuthForm = () => (
    <>
      <Graphics
        graphics={
          <div className="graphicsLogo">
            <ConnectPlugIcon />
            <BigQuery className="datasourceLogo" />
          </div>
        }
        title="Let's connect to BigQuery"
        description={
          !isEditConnectionId ? (
            <span>
              Authorize with your Google account or{" "}
              <StyledLink onClick={handleSwitchToServiceAccount}>
                connect using a Service Account
              </StyledLink>
            </span>
          ) : (
            "Authorize Coefficient to connect to BigQuery"
          )
        }
      />

      {popupBlocked && (
        <WarningMessage
          title={WARNING_TITLES.POPUP_BLOCKED}
          message={WARNING_MESSAGES.POPUP_BLOCKED}
        />
      )}

      <OAuthCard>
        <StyledLabel style={{ margin: 0 }}>
          GCP Project ID
          <Input
            onChange={e => setOauthProjectId(e.currentTarget.value)}
            value={oauthProjectId}
            disabled={isOAuthInProgress}
            placeholder="e.g. my-bigquery-project"
          />
          <div className="help-text">
            Follow{" "}
            <a href={PROJECT_ID_DOC_URL} target="_blank" rel="noreferrer">
              these steps
            </a>{" "}
            to locate your BigQuery project ID from the Google Cloud Platform Console.
          </div>
        </StyledLabel>

        <OAuthButtonWrapper>
          <Button
            type="primary"
            disabled={!oauthProjectId || isOAuthInProgress}
            onClick={onOAuthClick}
          >
            {isOAuthInProgress ? <DotsLoader style={{ margin: "auto" }} /> : "Authorize"}
          </Button>
          {isOAuthInProgress && (
            <span className="cancel-link" onClick={cancelOAuth}>
              Cancel
            </span>
          )}
        </OAuthButtonWrapper>
      </OAuthCard>
    </>
  );

  const renderServiceAccountForm = () => (
    <>
      {!isEditConnectionId && (
        <Graphics
          graphics={
            <div className="graphicsLogo">
              <ConnectPlugIcon />
              <BigQuery className="datasourceLogo" />
            </div>
          }
          title={`Let's Connect to BigQuery`}
          description={
            <span>
              Connect using a Service Account or{" "}
              <StyledLink onClick={handleSwitchToOAuth}>sign in with Google</StyledLink>
            </span>
          }
        />
      )}

      {renderDataSourceItem?.({
        dataSource,
        setAvailableSchemas,
        isEditConnectionId: Boolean(isEditConnectionId),
      })}

      <InnerPanel>
        <div>
          <div className={"title"}>How to find your connection details</div>

          <div className="description">
            To locate your BigQuery project ID and Service Account Email from Google Cloud Platform
            Console,{" "}
            <a onClick={() => setShowServiceAccountCredentialsHelp(true)}>follow these steps</a>
          </div>
        </div>
      </InnerPanel>

      <StyledLabel>
        GCP Project ID
        <Input
          onChange={e => setGcpProjectId(e.currentTarget.value)}
          value={gcpProjectId}
          disabled={readOnly}
          error={uiErrors.gcpProjectId}
          placeholder="eg: coefficient"
        />
      </StyledLabel>

      <StyledLabel>
        Service Account Email
        <Input
          onChange={e => setServiceAccountEmail(e.currentTarget.value)}
          value={serviceAccountEmail}
          disabled={readOnly}
          error={uiErrors.serviceAccountEmail}
          placeholder="eg: bigquery@coefficient.iam.gserviceaccount.com"
        />
      </StyledLabel>

      <div>
        <StyledLabel style={{ marginBottom: 8 }}>JSON Key</StyledLabel>

        <StyledUpload
          disabled={readOnly}
          accept=".json"
          onRemove={() => onFileDelete()}
          beforeUpload={beforeUpload}
        >
          {showUploadJsonButton && (
            <UploadButton error={uiErrors.clientPrivateKey}>Upload JSON Key</UploadButton>
          )}
        </StyledUpload>
      </div>

      <StyledLabel>
        <Button
          noPadding
          type="link"
          style={{ color: COLORS.black85, fontWeight: "normal" }}
          onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
        >
          <FlexRow>
            <span>Advanced settings</span>

            <ChevronDown
              fill="inhert"
              style={showAdvancedSettings ? { transform: "rotate(180deg)", marginTop: 2 } : {}}
            />
          </FlexRow>
        </Button>
      </StyledLabel>

      {showAdvancedSettings && (
        <StyledLabel>
          <LimitSchemasInput
            alias="dataset"
            disabled={readOnly}
            dataSourceId={dataSource?.data_source_id}
            availableSchemas={availableSchemas}
            selectedSchemas={selectedSchemas}
            setSelectedSchemas={setSelectedSchemas}
            schemaLoading={schemaLoading}
            schemaError={schemaError}
            reloadSchema={reloadSchema}
          />
        </StyledLabel>
      )}
    </>
  );

  return (
    <>
      <StyledContent>
        {connectionError != null && (
          <ErrorMessage
            screen="authorizeBigQuery"
            action={authMode === "oauth" ? onOAuthClick : connectBigQuery}
            actionText="Try again"
            title={connectionErrorTitle}
            message={connectionError}
            dataSourceType={DataSourceType.Bigquery}
          />
        )}

        {authMode === "oauth" ? renderOAuthForm() : renderServiceAccountForm()}
      </StyledContent>
      <StyledFooter>
        {authMode === "service_account" && !readOnly && (
          <ButtonSection noBoxShadow={true}>
            <Button
              disabled={loading || !(gcpProjectId && serviceAccountEmail && clientKeyJson)}
              target="_blank"
              type="primary"
              onClick={connectBigQuery}
            >
              {loading ? (
                <DotsLoader style={{ margin: "auto" }} />
              ) : (
                <div>{isEditConnectionId ? "Save" : "Connect"}</div>
              )}
            </Button>
          </ButtonSection>
        )}

        {!isEditConnectionId && <SecurityInfo />}
      </StyledFooter>
      <ConfirmBox
        cancelText={"Got it"}
        title={`Locate your JSON Key`}
        visible={showServiceAccountCredentialsHelp}
        onCancel={() => {
          setShowServiceAccountCredentialsHelp(false);
        }}
      >
        <p>
          To configure a new Service account or generate a JSON key, head to the Google Cloud
          Platform Console{" "}
          <a
            href="https://console.cloud.google.com/iam-admin/serviceaccounts/create"
            target="_blank"
          >
            here
          </a>
        </p>
      </ConfirmBox>
    </>
  );
};
